import { useMemo, useState } from 'react';
import { ChevronDown, Plus, Search, X } from 'lucide-react';
import {
  PRACTICE_AREAS,
  PRACTICE_AREA_LABELS,
  SPECIALTIES,
  SPECIALTY_LABELS,
  SPECIALTY_AREA,
  type Specialty,
  type PracticeArea,
} from '@parecer/shared';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface SpecialtyPickerProps {
  selected: Specialty[];
  onChange: (next: Specialty[]) => void;
  tags?: string[];
  onTagsChange?: (next: string[]) => void;
  /** Maximum number of specialties (default 15). */
  max?: number;
  /** Maximum number of free-form tags (default 20). */
  maxTags?: number;
  /** Optional helper passed by parent — disables tag input entirely. */
  hideTags?: boolean;
}

/**
 * Hierarchical specialty picker — search bar on top, specialties grouped by
 * practice area (collapsible), free-form tags below for niches that don't
 * fit any enum (SAFE, NDA, vesting, Lei do Bem…).
 *
 * Used in: lawyer signup, lawyer profile editor.
 */
export function SpecialtyPicker({
  selected,
  onChange,
  tags = [],
  onTagsChange,
  max = 15,
  maxTags = 20,
  hideTags = false,
}: SpecialtyPickerProps) {
  const [search, setSearch] = useState('');
  const [collapsedAreas, setCollapsedAreas] = useState<Set<PracticeArea>>(new Set());
  const [tagDraft, setTagDraft] = useState('');

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  // Build search match (case+accent-insensitive). When searching, auto-expand
  // every area with a hit.
  const normalized = useMemo(
    () =>
      search
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, ''),
    [search],
  );

  const specialtiesByArea = useMemo(() => {
    const m = new Map<PracticeArea, Specialty[]>();
    for (const s of SPECIALTIES) {
      const a = SPECIALTY_AREA[s];
      if (!m.has(a)) m.set(a, []);
      // Filter by search
      const label = SPECIALTY_LABELS[s]
        .toLowerCase()
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '');
      if (!normalized || label.includes(normalized)) m.get(a)!.push(s);
    }
    return m;
  }, [normalized]);

  function toggle(s: Specialty): void {
    if (selectedSet.has(s)) {
      onChange(selected.filter((x) => x !== s));
    } else {
      if (selected.length >= max) return; // hit max
      onChange([...selected, s]);
    }
  }

  function toggleArea(a: PracticeArea): void {
    setCollapsedAreas((prev) => {
      const next = new Set(prev);
      if (next.has(a)) next.delete(a);
      else next.add(a);
      return next;
    });
  }

  function addTag(): void {
    const t = tagDraft.trim();
    if (!t || t.length < 2 || t.length > 40) return;
    if (tags.includes(t)) {
      setTagDraft('');
      return;
    }
    if (tags.length >= maxTags) return;
    onTagsChange?.([...tags, t]);
    setTagDraft('');
  }

  function removeTag(t: string): void {
    onTagsChange?.(tags.filter((x) => x !== t));
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          type="search"
          placeholder="Buscar especialidade (ex: LGPD, divórcio, Web3…)"
          className="pl-9"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {selected.length} / {max} selecionadas
        </span>
        {selected.length > 0 && (
          <button
            type="button"
            className="hover:text-foreground"
            onClick={() => onChange([])}
          >
            Limpar tudo
          </button>
        )}
      </div>

      {/* Practice areas */}
      <div className="space-y-2">
        {PRACTICE_AREAS.map((area) => {
          const list = specialtiesByArea.get(area) ?? [];
          if (list.length === 0) return null;
          // While the user is searching, force every matching area to be open.
          const collapsed = !normalized && collapsedAreas.has(area);
          const selectedInArea = list.filter((s) => selectedSet.has(s)).length;
          return (
            <div key={area} className="rounded-md border border-border/60">
              <button
                type="button"
                onClick={() => toggleArea(area)}
                className="w-full flex items-center justify-between px-3 py-2.5 text-sm font-medium hover:bg-muted/40"
              >
                <span className="flex items-center gap-2">
                  {PRACTICE_AREA_LABELS[area]}
                  {selectedInArea > 0 && (
                    <Badge variant="accent" className="text-[10px] h-4 px-1.5">
                      {selectedInArea}
                    </Badge>
                  )}
                </span>
                <ChevronDown
                  className={cn(
                    'h-4 w-4 text-muted-foreground transition-transform',
                    collapsed && '-rotate-90',
                  )}
                />
              </button>
              {!collapsed && (
                <div className="px-3 pb-3 pt-1 flex flex-wrap gap-1.5">
                  {list.map((s) => {
                    const active = selectedSet.has(s);
                    const disabled = !active && selected.length >= max;
                    return (
                      <button
                        key={s}
                        type="button"
                        disabled={disabled}
                        onClick={() => toggle(s)}
                        className={cn(
                          'inline-flex items-center rounded-full border px-3 py-1.5 text-xs transition-colors',
                          active
                            ? 'border-accent bg-accent/15 text-foreground font-medium'
                            : 'border-border text-muted-foreground hover:border-accent/40 hover:text-foreground',
                          disabled && 'opacity-40 cursor-not-allowed',
                        )}
                      >
                        {SPECIALTY_LABELS[s]}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        {Array.from(specialtiesByArea.values()).every((l) => l.length === 0) && (
          <p className="text-sm text-muted-foreground italic text-center py-6">
            Nenhuma especialidade combina com "{search}".
          </p>
        )}
      </div>

      {/* Tags */}
      {!hideTags && onTagsChange && (
        <div className="rounded-md border border-dashed border-border/80 p-4 space-y-3">
          <div>
            <p className="text-sm font-medium">Tags livres (opcional)</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Nichos hiper-específicos: SAFE, NDA bilateral, vesting, Lei do Bem, Marco Civil, MEI…
              Aparecem como chips no seu card e refinam a busca da IA.
            </p>
          </div>
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {tags.map((t) => (
                <span
                  key={t}
                  className="inline-flex items-center gap-1 rounded-full border border-border/60 bg-muted/40 pl-2.5 pr-1 py-0.5 text-xs"
                >
                  {t}
                  <button
                    type="button"
                    onClick={() => removeTag(t)}
                    className="rounded-full hover:bg-background/80 p-0.5"
                    aria-label={`Remover tag ${t}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <Input
              value={tagDraft}
              onChange={(e) => setTagDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addTag();
                }
              }}
              placeholder={tags.length >= maxTags ? 'Limite atingido' : 'Nova tag e Enter'}
              disabled={tags.length >= maxTags}
              maxLength={40}
              className="text-sm"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addTag}
              disabled={!tagDraft.trim() || tags.length >= maxTags}
              className="gap-1"
            >
              <Plus className="h-3.5 w-3.5" />
              Adicionar
            </Button>
          </div>
          <p className="text-[11px] text-muted-foreground">
            {tags.length} / {maxTags} tags.
          </p>
        </div>
      )}
    </div>
  );
}
